import { useState, useEffect } from "react";
import { FlaskConical, Loader2 } from "lucide-react";
import { Button } from "@um/ui/components/ui";
import { testManagerApi } from "../firebase";
import { useAuth } from "../hooks/useAuth";
import { toast } from "sonner";
import { logException } from "../telemetry";
import { getBaseUrl, type TestUser } from "@um/contracts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

function getInitials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

interface TestLoginButtonProps {
  dontCreateAccount?: boolean;
  onSuccess?: () => void;
  /** When provided, called with email and password instead of signInWithEmail. */
  onSignIn?: (email: string, password: string) => Promise<void>;
}

export default function TestLoginButton({
  dontCreateAccount,
  onSuccess,
  onSignIn,
}: TestLoginButtonProps = {}) {
  const { signInWithEmail, selectedTestGroupId, setSelectedTestGroupId } =
    useAuth();

  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<TestUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [signingIn, setSigningIn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !selectedTestGroupId) return;
    setLoading(true);
    setError(null);
    setUsers([]);
    testManagerApi({ action: "getUsers", resourceGroupId: selectedTestGroupId })
      .then((res) => setUsers(res.data as TestUser[]))
      .catch((err) => {
        logException(err);
        setError("Failed to load test credentials. Close this window and try again.");
        toast.error("Failed to load test credentials");
      })
      .finally(() => setLoading(false));
  }, [open, selectedTestGroupId]);

  if (!selectedTestGroupId) return null;
  const manageCredentialsUrl = `${getBaseUrl("admin")}/test-resources/${selectedTestGroupId}`;

  const handleLogin = async (user: TestUser) => {
    try {
      setSigningIn(user.uid);
      setError(null);
      if (onSignIn) {
        await onSignIn(user.email, user.password);
      } else {
        await signInWithEmail(user.email, user.password, dontCreateAccount);
      }
      onSuccess?.();
    } catch (err) {
      logException(err);
      setError("Failed to sign in as this test user. Check the credentials and try again.");
      toast.error("Failed to sign in as test user");
    } finally {
      setSigningIn(null);
    }
  };

  return (
    <>
      <div className="test-login space-y-1">
        <Button
          variant="secondary"
          type="button"
          className="test-login-trigger w-full py-6 text-base flex gap-3 justify-center items-center"
          onClick={() => setOpen(true)}
        >
          <FlaskConical className="w-5 h-5" />
          Test User ({selectedTestGroupId})
        </Button>
        <button
          type="button"
          onClick={() => setSelectedTestGroupId(null)}
          className="test-login-exit w-full text-center text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
        >
          Exit test mode
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="test-login-dialog w-full max-w-lg max-h-[80vh] overflow-y-auto"
          overlayClassName="test-login-overlay"
        >
          <DialogHeader>
            <DialogTitle>Test Credentials</DialogTitle>
            <DialogDescription>
              Select a test account to sign in with.
            </DialogDescription>
          </DialogHeader>

          {error && <p className="test-login-error text-sm text-destructive" role="alert">{error}</p>}

          <Button
            type="button"
            className="test-login-manage"
            variant="outline"
            size="sm"
            onClick={() => {
              window.location.assign(manageCredentialsUrl);
            }}
          >
            Manage Test Users
          </Button>

          {loading ? (
            <div className="test-login-loading flex items-center justify-center py-8" role="status" aria-label="Loading test users">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : users.length === 0 && !error ? (
            <p className="test-login-empty text-center text-sm text-muted-foreground py-8">
              No test credentials found.
            </p>
          ) : (
            <div className="test-login-users space-y-2">
              {users.map((user) => (
                <button
                  key={user.uid}
                  type="button"
                  onClick={() => handleLogin(user)}
                  disabled={signingIn !== null}
                  className="test-login-user w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent disabled:opacity-50"
                >
                  <div className="test-login-avatar relative h-10 w-10 shrink-0 overflow-hidden rounded-full bg-muted">
                    {user.pictureUrl ? (
                      <img
                        src={user.pictureUrl}
                        alt={user.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-xs font-medium">
                        {getInitials(user.name)}
                      </span>
                    )}
                  </div>
                  <div className="test-login-user-details min-w-0 flex-1">
                    <p className="text-sm font-medium leading-none">
                      {user.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {user.email}
                    </p>
                    {user.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {user.description}
                      </p>
                    )}
                  </div>
                  {signingIn === user.uid && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
